import { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useFeature } from '@growthbook/growthbook-react';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { PublicGridEligibilityInput, Query } from '@updater/consumer-graph';
import { useGetUserInfoQuery } from 'flows/core/hooks/use-get-user-info-query';
import {
  DOMAIN,
  ELIGIBILITY_FAILURE,
  ELIGIBILITY_TIMEOUT_MS,
  OBJECT,
  PUBLIC_GRID_FEATURE_FLAG,
  VERB,
} from '../constants';

/**
 * Written here rather than as a `.graphql` document for the same reason as the
 * embed query: codegen only reads `.graphql`, so keeping this in TS means no
 * `graphql:sync` run is needed and no duplicate-operation collision later. The
 * types still come from `@updater/consumer-graph`.
 */
export const PublicGridEligibilityQuery = gql`
  query publicGridIsEligible($input: PublicGridEligibilityInput!) {
    publicGrid {
      isEligible(input: $input) {
        eligibility {
          eligible
          zip
        }
        error {
          code
          message
        }
      }
    }
  }
`;

type PublicGridEligibilityQueryData = Pick<Query, 'publicGrid'>;
type PublicGridEligibilityQueryVariables = {
  input: PublicGridEligibilityInput;
};

/** Public Grid takes a five-digit ZIP. Anything else is not worth a call. */
const toZip = (postalCode?: string | null): string | undefined => {
  const digits = postalCode?.replace(/\D/g, '').slice(0, 5);
  return digits?.length === 5 ? digits : undefined;
};

export interface PublicGridEligibilityResult {
  /**
   * `true` only when Public Grid can actually transact for this address.
   * `undefined` until that is known, so callers can hold the UI rather than
   * guess — see the note on failing closed below.
   */
  eligible?: boolean;
  loading: boolean;
}

/**
 * Whether Public Grid can set up utilities at the resident's new address.
 *
 * Their coverage is per-provider, not national: a ZIP can have a utility they
 * know about but cannot transact with, and minting an embed for such an address
 * produces a flow that dead-ends on the resident — with our own electric UI
 * suppressed behind them. So this is asked before the card is offered, not
 * after it is tapped.
 *
 * Failure closes: a partner error, a missing move, or an unusable ZIP all
 * resolve to `eligible: false`, which restores our own utility task. Showing a
 * resident the utility list they could always use is a far smaller harm than
 * showing them a flow that cannot serve their address.
 *
 * A hang closes the same way, on a timer. Apollo has no timeout, and every
 * caller holds its loading flag until this resolves, so a partner that neither
 * answers nor errors would hold the whole checklist indefinitely. After
 * {@link ELIGIBILITY_TIMEOUT_MS} we stop waiting and report ineligible — and
 * stay there, even if the answer lands afterwards: flipping the electric UI
 * under a resident already reading it is the exact flicker those loading holds
 * exist to prevent. Each of the three failures fires one `errored` event
 * carrying the reason, since the UI makes them indistinguishable.
 */
export const usePublicGridEligibility = (): PublicGridEligibilityResult => {
  const { on: publicGridEnabled } = useFeature(PUBLIC_GRID_FEATURE_FLAG);
  // No `pageName`: this runs behind the checklist, the daisy chain and two ROB
  // pages, so the tracker's `document.title` default names the real one.
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.PUBLIC_GRID,
  });

  // The move's destination, from the query every caller of this hook already
  // runs — asking for the user again here would make the hook unmockable
  // wherever consumer-graph is only partially mocked.
  const { loading: loadingUser, data: userData } = useGetUserInfoQuery();

  const zip = toZip(
    userData?.getUser?.user?.currentMove?.toAddress?.postalCode
  );

  // Nothing is asked of Public Grid while the flag is off: an ineligible answer
  // and an un-asked question both end in the same place, and the call spends
  // their rate-limit budget.
  const skip = !publicGridEnabled || !zip;

  const { loading, data, error } = useQuery<
    PublicGridEligibilityQueryData,
    PublicGridEligibilityQueryVariables
  >(PublicGridEligibilityQuery, {
    variables: zip ? { input: { zip } } : undefined,
    skip,
  });

  // Only the partner call is timed. A slow user query is our own infrastructure
  // and already holds every caller's page regardless of Public Grid, so putting
  // it on this clock would report Public Grid for someone else's latency.
  const waitingOnPartner = !skip && loading;

  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!waitingOnPartner) return undefined;
    const timer = setTimeout(() => setTimedOut(true), ELIGIBILITY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [waitingOnPartner]);

  const partnerError = data?.publicGrid?.isEligible?.error;
  const failure = timedOut
    ? ELIGIBILITY_FAILURE.TIMEOUT
    : error
    ? ELIGIBILITY_FAILURE.TRANSPORT_ERROR
    : partnerError
    ? ELIGIBILITY_FAILURE.PARTNER_ERROR
    : undefined;

  // One event per failure, not one per render. The ref is never reset: a
  // resident whose check fails is reported once for as long as the hook lives,
  // however many callers re-render around it.
  const reportedFailure = useRef(false);

  useEffect(() => {
    if (!failure || reportedFailure.current) return;
    reportedFailure.current = true;
    trackEvent({
      verb: VERB.ERRORED,
      object: OBJECT.ELIGIBILITY,
      details: { reason: failure },
    });
  }, [failure, trackEvent]);

  if (!publicGridEnabled) return { eligible: false, loading: false };

  // Checked before the loading hold, which is the whole point: the request may
  // still be in flight, and we are no longer waiting for it.
  if (timedOut) return { eligible: false, loading: false };

  if (loadingUser || waitingOnPartner) return { loading: true };

  // A resident with no usable ZIP is not someone we can ask about, so they are
  // treated as ineligible rather than left in a permanent loading state.
  if (!zip) return { eligible: false, loading: false };

  if (failure) return { eligible: false, loading: false };

  const result = data?.publicGrid?.isEligible;

  return { eligible: result?.eligibility?.eligible === true, loading: false };
};
